import { Color } from '../entities/Color';
import { ComponentKewlox } from './ComponentKewlox';
import {
  IntermediateShelfEntity,
  PropsComponent,
  PropsGeneral,
  SearchInfo,
} from '../Data';
import { DataStorage, getColors, getPiecesProps } from '../utils/dataStorage';
import {
  GLASS_COLOR,
  MINIMUM_HEIGHT_SPACE_AFTER_ADDING_INTERMEDIATE_SHELF,
  PaneMaterial,
  PlateType,
  STANDARD_COLOR,
  THICKNESS_INTER_SHELF_GLASS,
  THICKNESS_INTER_SHELF_MDF_AGGLO,
} from '../constants';
import { CustomPiece } from './CustomPiece';
import { Shelf } from '../entities/Shelf';
import {
  computePiecePrice,
  computeWeightWithDensity,
  getPropsKey,
} from '../utils/helpers';
import { getMaterialColor } from '../utils/colorHelper';

export class IntermediateShelf extends ComponentKewlox {
  readonly width: number;
  readonly depth: number;
  readonly color: Color;
  readonly doubleTap: boolean;

  static _lookup = ['width', 'depth'];

  static _gapWidth = -20;
  static _gapWidthGlass = -32;

  static _gapDepth = -38;
  /*
  When glass, real depth is complex to compute because a standard plate is used as an inter. shelf. 
  So, we need to compute real sizes based on plate rules.
  We have 3 cases:
    1) All depths AND width lower than 620 (standard width for plate type PP)
    2) Depth lower than 320 AND width is minimum 620 (standard width for plate type DP)
    3) Other depths AND width is minimum 620 (standard width for plate type DP)
  */
  static _gapDepthGlassCase1 = -32;
  static _gapDepthGlassCase2 = -38;
  static _gapDepthGlassCase3 = -40;

  constructor(
    id: number,
    name: string,
    reference: string,
    price: number,
    weigth: number,
    forSale: boolean,
    visible: boolean,
    height: number,
    width: number,
    depth: number,
    color: Color,
    doubleTap = false,
  ) {
    super(id, name, reference, price, weigth, forSale, visible, height);
    this.width = width;
    this.depth = depth;
    this.color = color;
    this.doubleTap = doubleTap;
  }

  get typeComponent() {
    return 'IntermediateShelf';
  }

  static fromJSON(json: IntermediateShelfEntity): IntermediateShelf {
    let objColor: Color;
    if (typeof json.color === 'number') {
      const colors = getColors();
      objColor = colors[0];
      for (const colorItem of colors) {
        if (colorItem.id === json.color) {
          objColor = colorItem;
        }
      }
    } else {
      objColor = json.color as Color;
    }

    const o = Object.create(IntermediateShelf.prototype);
    return Object.assign(o, this, json, {
      color: objColor,
    });
  }

  static reviver(key: string, value: any): any {
    return key === '' ? IntermediateShelf.fromJSON(value) : value;
  }

  compareTo(otherComponent: ComponentKewlox): number {
    return (
      this.width - (otherComponent as IntermediateShelf).width ||
      this.depth - (otherComponent as IntermediateShelf).depth
    );
  }

  includeInto(otherComponent: ComponentKewlox): boolean {
    return (
      this.width - (otherComponent as IntermediateShelf).width >= 0 &&
      this.depth - (otherComponent as IntermediateShelf).depth >= 0
    );
  }

  static getSearchInfos(
    width: number,
    depth: number,
    color: Color,
    doubleTap = false,
  ): SearchInfo {
    const height = 18;
    return {
      lookup: this._lookup,
      key: { height, width, depth, color: color.id, doubleTap },
    };
  }

  getKey(): string {
    return IntermediateShelf.generateKey(
      this.width,
      this.depth,
      this.color,
      this.doubleTap,
    );
  }

  static generateKey(
    width: number,
    depth: number,
    color: Color,
    doubleTap: boolean,
  ) {
    const searchInfos = IntermediateShelf.getSearchInfos(
      width,
      depth,
      color,
      doubleTap,
    );
    return JSON.stringify(searchInfos.key);
  }

  static standardIntermediateShelf(
    width: number,
    depth: number,
    position: number,
  ): CustomPiece<IntermediateShelf> {
    /**
     * Return a standard Door as custom piece
     */
    const colors = DataStorage.colors;
    const color = colors.find(item => item.reference === STANDARD_COLOR);
    const pieces = DataStorage.pieces;
    const searchInfo = IntermediateShelf.getSearchInfos(
      width,
      depth,
      color as Color,
    );
    return pieces.intermediatesShelves.getCustom(searchInfo, position);
  }

  static checkAllowHeight(
    position: number,
    shelf: Shelf,
    height: number,
  ): boolean {
    /**
     * Check if we can update the IS to the new height
     * If the height of all the IS are too hight, the check fail
     */
    let totalHeight = height;
    for (const entryIs of shelf.intermediatesShelves.filter(
      e => e.position !== position,
    )) {
      totalHeight += entryIs.height;
    }
    return (
      totalHeight <=
      shelf.height - MINIMUM_HEIGHT_SPACE_AFTER_ADDING_INTERMEDIATE_SHELF
    );
  }

  get realWidth() {
    return IntermediateShelf.computeRealWidth(this.width);
  }

  get realHeight() {
    return IntermediateShelf.computeRealHeight(
      0,
      undefined,
      this.color.reference === GLASS_COLOR,
    );
  }

  get realDepth() {
    return IntermediateShelf.computeRealDepth(
      this.depth,
      undefined,
      this.width < 620 ? PlateType.PanePane : PlateType.DoorsPane,
      this.color.reference === GLASS_COLOR,
    );
  }

  static computeRealDepth(
    depth: number,
    isNatural?: boolean,
    plateType?: PlateType | undefined,
    glass?: boolean,
  ): number {
    switch (plateType) {
      case PlateType.PanePane:
        if (glass) return depth + IntermediateShelf._gapDepthGlassCase1;
        return depth + IntermediateShelf._gapDepth;
      case PlateType.DoorsPane:
        if (depth < 320) {
          if (glass) return depth + IntermediateShelf._gapDepthGlassCase2;
          return depth + IntermediateShelf._gapDepth;
        }
        if (glass) return depth + IntermediateShelf._gapDepthGlassCase3;
        return depth + IntermediateShelf._gapDepth;
      default:
        return depth + IntermediateShelf._gapDepth;
    }
  }

  static computeRealHeight(
    height: number,
    plateType?: PlateType | undefined,
    glass?: boolean,
  ): number {
    if (glass) return THICKNESS_INTER_SHELF_GLASS;
    return THICKNESS_INTER_SHELF_MDF_AGGLO;
  }

  static computeRealWidth(
    width: number,
    glass?: boolean,
    largeDoor?: boolean,
  ): number {
    return glass
      ? width + IntermediateShelf._gapWidthGlass
      : width + IntermediateShelf._gapWidth;
  }

  static getProperties(
    material: PaneMaterial,
    color: Color,
  ): PropsComponent | void {
    const key = getPropsKey(material, color);

    if (getPiecesProps().tablets.hasOwnProperty(key)) {
      return getPiecesProps().tablets[key];
    }
  }

  static computeBasePrice(
    width: number,
    depth: number,
    doubleTap: boolean,
    material: PaneMaterial,
    props: PropsComponent,
    propsGen: PropsGeneral,
  ) {
    /**
     * This formula came from the PRO100 Excel linked file and has been adapted
     *
     * Formule :
     *
     * surface in µ
     *
     */

    width = width > props.minDim ? width : props.minDim;
    depth = depth > props.minDim ? depth : props.minDim;

    const widthMicr = width / 1000;
    const depthMicr = depth / 1000;

    const surface = widthMicr * depthMicr;
    const perimeter = (widthMicr + depthMicr) * 2;

    const k1 = props.k1 ?? 0;
    const k2 = props.k2 ?? 0;
    const k3 = props.k3 ?? 0;
    const k4 = props.k4 ?? 0;
    const k5 = props.k5 ?? 0;

    const interShelfWorkforce = doubleTap
      ? propsGen.interShelfDoubleTapWorkforce + propsGen.interShelfWorkforce
      : propsGen.interShelfWorkforce;

    const lengthTap = doubleTap ? 2 * widthMicr : widthMicr;

    let price =
      material === PaneMaterial.Glass
        ? (surface * k1 + k2 + perimeter * k4) * k3
        : (surface * k1 + k2 + lengthTap * k5 + interShelfWorkforce) * k3;

    price *= propsGen.coef;
    price = Math.round(price * 100) / 100;

    return price;
  }

  public computePrice(customPiece: CustomPiece<ComponentKewlox>): number {
    const props: PropsComponent | void = IntermediateShelf.getProperties(
      getMaterialColor(this.color),
      this.color,
    );
    const propsGen: PropsGeneral = getPiecesProps().general;

    if (!props) return 0;

    const priceForDimensions = IntermediateShelf.computeBasePrice(
      customPiece.realWidth,
      customPiece.realDepth,
      this.doubleTap,
      getMaterialColor(this.color),
      props,
      propsGen,
    );

    const priceForBase = IntermediateShelf.computeBasePrice(
      this.realWidth,
      this.realDepth,
      this.doubleTap,
      getMaterialColor(this.color),
      props,
      propsGen,
    );

    return computePiecePrice(
      priceForDimensions,
      priceForBase,
      customPiece.custom,
      customPiece.calibrated,
      propsGen,
    );
  }

  public computeWeight(customPiece: CustomPiece<ComponentKewlox>): number {
    const props: PropsComponent | void = IntermediateShelf.getProperties(
      getMaterialColor(this.color),
      this.color,
    );

    if (!props) return 0;

    return computeWeightWithDensity(
      customPiece.realWidth,
      customPiece.realHeight,
      customPiece.realDepth,
      props.density,
    );
  }
}
